import { pick } from 'lodash';
import * as Yup from 'yup';

// .jsx export — typed as any in TS, fine at runtime
import { getBaseConfiguredFieldsValidations } from '~/components/IncidentConfiguration/ConfiguredFields';
import type { OrganizationDocumentType } from '~/components/types/document-type';

/**
 * Filter a raw `configured_fields_values` dict down to only the keys defined on
 * the given document type. Used on submit (upload + edit dialogs) so that if a
 * user picks doc-type A, fills its configured fields, then switches to doc-type
 * B, the orphaned keys from A don't get sent to the server (where they would
 * be rejected as "Unknown additional data field").
 *
 * Returns an empty object when `documentType` is undefined or has no
 * configured_fields — the user hasn't picked a type, or the type has nothing
 * to render.
 */
export function filterConfiguredFieldsValuesForType(
  rawValues: Record<string, unknown> | null | undefined,
  documentType: OrganizationDocumentType | null | undefined
): Record<string, unknown> {
  const allowedKeys = (documentType?.configuration?.configured_fields ?? []).map((field) => field.id);
  return pick(rawValues ?? {}, allowedKeys);
}

/**
 * Build a Yup object schema for `configured_fields_values` based on the selected
 * document type's `configured_fields` definition. Mirrors the FNOL incident
 * details pattern (`getBaseConfiguredFieldsValidations`) so mandatory fields are
 * caught client-side, before the user hits submit, instead of round-tripping to
 * the BE for a 400.
 *
 * Returns an empty `Yup.object()` when `documentType` is undefined or has no
 * configured_fields — no field-level rules to enforce.
 */
export function buildConfiguredFieldsValuesYupShape(documentType: OrganizationDocumentType | null | undefined) {
  const configuredFields = documentType?.configuration?.configured_fields ?? [];
  const validations = getBaseConfiguredFieldsValidations(configuredFields) as Record<string, Yup.AnySchema>;
  return Yup.object().shape(validations);
}
